// ✅請把下列內容「完整覆蓋」到：ui/pages/ComplianceControlCenterPage.tsx
//（整支檔案取代，不要 patch，不要省略）

// ui/pages/ComplianceControlCenterPage.tsx
import React, { useState, useEffect, useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import { isSuperUser, getDeptCodeByEmail, canDeleteComplianceProject } from "../config/roles";
import { getDeptNameByCode, DEPARTMENTS } from "../constants/departments";
import {
  listComplianceCards,
  seedInitialProjectCards,
  createComplianceCard,
  deleteComplianceCard,
  getLatestCrawlerRun,
  runComplianceCrawlerNow,
  refreshSeedCards,
  isComplianceHistoryStatus,
} from "../services/complianceService";
import { ComplianceCard } from "../components/compliance/ComplianceCard";
import { TrackedAuthoritiesDialog } from "../components/compliance/TrackedAuthoritiesDialog";
import { ComplianceCard as IComplianceCard, CrawlerRunLog, ComplianceCardStatus } from "../types/compliance";
import { useToast } from "../components/Toast";
import { Modal } from "../components/Modal";
import { useAiContext } from "../ai/AiContext";
import { canViewData } from "../utils/dataAccess";

// 「公司類別 / 主題標籤」對照表（由後端 computeRiskEvaluation 產出 topicTags）
const TOPIC_TAG_LABELS: Record<string, string> = {
  ecommerce: "電商",
  ads: "廣告/行銷",
  privacy: "個資",
  security: "資安",
  labor: "勞動",
  food: "食品/食安",
  finance: "金融/金流",
  fair_trade: "公平交易",
  consumer: "消費者保護",
  healthcare: "醫療/藥妝",
  environment: "環保",
  ip: "智財",
  other: "其他",
};

function renderTopicTags(card: IComplianceCard): string {
  const tags = ((card as any)?.sectionB?.topicTags as string[]) || [];
  if (!Array.isArray(tags) || tags.length === 0) return "";
  const mapped = tags.map((t) => TOPIC_TAG_LABELS[t] || t);
  // 去重並限制長度，避免卡片被撐爆
  return Array.from(new Set(mapped)).slice(0, 5).join("、");
}

function normalizeComplianceCardForUI(card: IComplianceCard): IComplianceCard {
  const c: any = { ...card };

  c.sectionA = { ...(card as any).sectionA };
  if (!c.sectionA) c.sectionA = {};
  if (!Array.isArray(c.sectionA.legalBasis)) c.sectionA.legalBasis = [];
  if (!Array.isArray(c.sectionA.attachments)) c.sectionA.attachments = [];
  if (typeof c.sectionA.title !== "string") c.sectionA.title = c.sectionA.title ? String(c.sectionA.title) : "";

  c.sectionB = { ...(card as any).sectionB };
  if (!c.sectionB) c.sectionB = {};
  if (!Array.isArray(c.sectionB.attachments)) c.sectionB.attachments = [];

  if (!Array.isArray(c.coAssistSections)) c.coAssistSections = [];
  c.coAssistSections = c.coAssistSections.map((s: any) => {
    const sec = { ...(s || {}) };
    if (!Array.isArray(sec.attachments)) sec.attachments = [];
    return sec;
  });

  return c as IComplianceCard;
}

function CrawlerStatusBanner({
  canRun,
  onAfterRun,
}: {
  canRun: boolean;
  onAfterRun: () => void;
}) {
  const [log, setLog] = useState<CrawlerRunLog | null>(null);
  const [running, setRunning] = useState(false);
  const { showToast } = useToast();

  const load = async () => {
    try {
      const latest = await getLatestCrawlerRun();
      setLog(latest);
      return latest;
    } catch {
      setLog(null);
      return null;
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRunNow = async () => {
    if (!confirm("確定要立刻執行爬蟲嗎？此操作約需 1-2 分鐘。")) return;
    setRunning(true);
    try {
      await runComplianceCrawlerNow();
      showToast("爬蟲已觸發，請稍候刷新結果…");
      await load();
      onAfterRun();
      showToast("爬蟲執行完成");
    } catch (e: any) {
      showToast("執行失敗: " + e.message, "error");
    } finally {
      setRunning(false);
    }
  };

  // ✅就算沒有 log 也要顯示（避免整塊消失=按鈕消失）
  const status = log?.status || "NO_LOG";
  const statusColor =
    status === "SUCCESS" ? "#10b981" : status === "FAILED" ? "#ef4444" : status === "NO_LOG" ? "#888" : "#fbbf24";
  const timeStr = log?.startedAt?.toDate ? log.startedAt.toDate().toLocaleString() : "尚無紀錄";
  const fetched = log?.fetchedCount ?? 0;
  const created = log?.createdCardsCount ?? 0;

  return (
    <div
      style={{
        marginBottom: 16,
        padding: "10px 16px",
        background: "#161616",
        border: "1px solid #333",
        borderRadius: 8,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <div style={{ fontSize: 12, color: "#ccc" }}>
        <span style={{ color: "#aaa", marginRight: 8 }}>每日 08:30 排程狀態</span>
        <span style={{ fontWeight: "bold", color: statusColor, marginRight: 12 }}>{status}</span>
        <span style={{ marginRight: 12 }}>Last Run: {timeStr}</span>
        <span>
          (Fetched: {fetched} / Created: {created})
        </span>
        {log?.errorMessage && <div style={{ color: "#ef4444", marginTop: 4 }}>Error: {log.errorMessage}</div>}
      </div>

      {/* ✅權限：kc / ops / gm / ad */}
      {canRun && (
        <button
          className="smallbtn"
          onClick={handleRunNow}
          disabled={running}
          style={{ fontSize: 11, padding: "4px 8px" }}
        >
          {running ? "執行中..." : "立刻執行爬蟲"}
        </button>
      )}
    </div>
  );
}

// Compact Grid Card Component
const ProjectGridItem = ({
  card,
  onClick,
  onDelete,
  canManage,
  isOwner,
}: {
  card: IComplianceCard;
  onClick: () => void;
  onDelete?: () => void;
  canManage: boolean;
  isOwner: boolean;
}) => {
  const riskColor = card.riskLevel === "HIGH" ? "#ef4444" : card.riskLevel === "MEDIUM" ? "#f59e0b" : "#10b981";

  const hasLink = !!card.sectionA?.announcementUrl;
  const attachmentCount = (card.sectionA?.attachments?.length || 0) + (card.sectionB?.attachments?.length || 0);

  const getStatusColor = (s: ComplianceCardStatus) => {
    if (s === "DRAFT") return "#666";
    if (s === "IN_REVIEW") return "#fbbf24";
    if (s === "APPROVED" || s === "COMPLETED") return "var(--ok)";
    if (s === "REJECTED" || s === "RETURNED") return "var(--danger)";
    return "#aaa";
  };

  const isEditableStatus = card.status === "DRAFT" || card.status === "RETURNED" || card.status === "NOT_STARTED";
  const showEdit = canManage || (isOwner && isEditableStatus);

  const showDelete = !!onDelete;

  const dateDisplay = card.sectionA?.dateText || (card.sectionA as any)?.date || "—";
  const agencyDisplay = (card as any).agencyName || "—";
  const displayRegulatorName = (card as any).agencyName || "—";
  const topicTagsText = renderTopicTags(card);

  let ownerRaw = (card as any).sectionB?.ownerDept || (card as any).ownerDept;
  let ownerDisplay = getDeptNameByCode(ownerRaw || "");
  if (!ownerDisplay && (card as any).sectionB?.ownerDeptSuggestionText) ownerDisplay = "待指定";
  if (!ownerDisplay) ownerDisplay = "待指定";

  return (
    <div
      style={{
        background: "#1e1e1e",
        border: "1px solid #333",
        borderRadius: 12,
        padding: 16,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        transition: "transform 0.2s, border-color 0.2s",
        minHeight: 180,
        position: "relative",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = "#555";
        e.currentTarget.style.transform = "translateY(-2px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "#333";
        e.currentTarget.style.transform = "translateY(0)";
      }}
    >
      <div onClick={onClick} style={{ cursor: "pointer", flex: 1 }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
          <span className="badge" style={{ color: riskColor, borderColor: riskColor, fontSize: 10, padding: "2px 6px" }}>
            {card.riskLevel}
          </span>
          <span
            className="badge"
            style={{
              background: "#333",
              fontSize: 10,
              border: "none",
              maxWidth: 120,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {agencyDisplay}
          </span>
        </div>

        <h3
          style={{
            fontSize: 15,
            fontWeight: "bold",
            color: "#fff",
            marginBottom: 6,
            lineHeight: 1.4,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {card.sectionA?.title}
        </h3>

        <div style={{ fontSize: 12, color: "#888", display: "flex", flexDirection: "column", gap: 4 }}>
          <div style={{ color: "#a5b4fc" }}>主管機關：{displayRegulatorName}</div>
          <div style={{ color: "#ccc" }}>主責：{ownerDisplay}</div>
          {topicTagsText ? <div style={{ color: "#9ca3af" }}>公司類別/主題：{topicTagsText}</div> : null}
          <div>公告：{dateDisplay}</div>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            狀態：<span style={{ color: getStatusColor(card.status) }}>{card.status}</span>
          </div>
        </div>
      </div>

      <div
        style={{
          marginTop: 12,
          paddingTop: 12,
          borderTop: "1px dashed #333",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div style={{ display: "flex", gap: 10, fontSize: 12, color: "#666" }}>
          {hasLink && <span title="有公告連結">🔗</span>}
          {attachmentCount > 0 && <span title="有附件">📎 {attachmentCount}</span>}
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {showEdit && (
            <button className="smallbtn" style={{ padding: "2px 8px", fontSize: 11 }} onClick={onClick}>
              詳情
            </button>
          )}
          {showDelete && (
            <button
              className="smallbtn"
              style={{ padding: "2px 8px", fontSize: 11, color: "var(--danger)", borderColor: "var(--danger)" }}
              onClick={(e) => {
                e.stopPropagation();
                onDelete?.();
              }}
            >
              刪除
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export function ComplianceControlCenterPage() {
  const { user } = useAuth();
  const { showToast } = useToast();
  const { requestDelete } = useAiContext();

  const [activeTab, setActiveTab] = useState<"INTEL" | "PROJECT" | "HISTORY">("PROJECT");
  const [allCards, setAllCards] = useState<IComplianceCard[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCrawlerConfig, setShowCrawlerConfig] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const [selectedCard, setSelectedCard] = useState<IComplianceCard | null>(null);

  const isSuper = isSuperUser(user?.email);
  const userDeptCode = getDeptCodeByEmail(user?.email);

  // ✅ 立刻執行爬蟲：kc / ops / gm / ad 可用
  const canRunCrawlerNow = ["ad@lg.app", "kc@lg.app", "ops@lg.app", "gm@lg.app"].includes(
    (user?.email || "").toLowerCase()
  );

  // ✅ 刪除權限（KC/AD）
  const canDelete = canDeleteComplianceProject(user?.email);

  // ✅ 只有 AD 能在 IN_REVIEW 仍刪除
  const canDeleteInReview = (user?.email || "").toLowerCase() === "ad@lg.app";

  const loadAllCards = async () => {
    setLoading(true);
    try {
      // Load once, then bucket in-memory to avoid tab counts "跳" and to keep tab labels stable.
      const list = await listComplianceCards(undefined, true);

      const visibleCards = list
        .filter((c) => canViewData(user as any, c))
        .map(normalizeComplianceCardForUI);

      setAllCards(visibleCards);
    } catch (e: any) {
      console.error(e);
      const msg = e?.message ? String(e.message) : String(e ?? "未知錯誤");
      showToast(`載入卡片失敗：${msg}`, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    seedInitialProjectCards().then(() => loadAllCards());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // When user context changes (login / dept switch), refresh the visible dataset.
    loadAllCards();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const { intelCards, projectCards, historyCards } = useMemo(() => {
    const intel = allCards.filter((c) => c.type === "intel" && !isComplianceHistoryStatus(c.status));
    const project = allCards.filter((c) => c.type === "project" && !isComplianceHistoryStatus(c.status));
    const history = allCards.filter((c) => isComplianceHistoryStatus(c.status));
    return { intelCards: intel, projectCards: project, historyCards: history };
  }, [allCards]);

  const cards = useMemo(() => {
    if (activeTab === "INTEL") return intelCards;
    if (activeTab === "HISTORY") return historyCards;
    return projectCards;
  }, [activeTab, intelCards, projectCards, historyCards]);

  const handleCreateProject = async () => {
    await createComplianceCard({
      type: "project",
      status: "DRAFT",
      riskLevel: "LOW",
      ownerDept: userDeptCode,
      sectionA: {
        title: "新專案 (請填寫)",
        date: null,
        announcementDate: null,
        subject: "",
        summary: "",
        sourceUrl: "",
        announcementUrl: "",
        legalBasis: [],
        attachments: [],
      },
      sectionB: { ownerDept: userDeptCode, content: "", attachments: [] },
      coAssistSections: [],
    } as any);
    loadAllCards();
  };

  const handleDelete = (card: IComplianceCard) => {
    if (!canDelete) return;

    if (card.status === "IN_REVIEW" && !canDeleteInReview) {
      alert("簽核中不得刪除");
      return;
    }

    requestDelete({
      targetType: "complianceCard",
      targetId: card.id,
      summary: card.sectionA?.title || "(未命名)",
      onConfirm: async () => {
        try {
          await deleteComplianceCard(card.id, user);
          showToast("已刪除專案");
          loadAllCards();
        } catch (e: any) {
          showToast(e.message, "error");
        }
      },
    });
  };

  const handleRefreshSource = async () => {
    if (!confirm("確定要更新 9 大義務卡片的最新公告資訊嗎？")) return;
    setRefreshing(true);
    try {
      const count = await refreshSeedCards();
      showToast(`已更新 ${count} 筆卡片資訊`);
      loadAllCards();
    } catch (e: any) {
      showToast("更新失敗: " + e.message, "error");
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <div style={{ marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, color: "#fff" }}>法遵情資與專案管控中心</h2>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {isSuper && (
            <button className="smallbtn" onClick={() => setShowCrawlerConfig(true)}>
              ⚙️ 管理主管機關
            </button>
          )}
        </div>
      </div>

      {activeTab === "INTEL" && <CrawlerStatusBanner canRun={canRunCrawlerNow} onAfterRun={loadAllCards} />}

      <div className="row" style={{ borderBottom: "1px solid #333", marginBottom: 16 }}>
        <button className={`navbtn ${activeTab === "INTEL" ? "active" : ""}`} onClick={() => setActiveTab("INTEL")}>
          法遵情資 ({intelCards.length})
        </button>
        <button className={`navbtn ${activeTab === "PROJECT" ? "active" : ""}`} onClick={() => setActiveTab("PROJECT")}>
          專案管控 ({projectCards.length})
        </button>
        <button className={`navbtn ${activeTab === "HISTORY" ? "active" : ""}`} onClick={() => setActiveTab("HISTORY")}>
          歷史彙編 ({historyCards.length})
        </button>
      </div>

      <div style={{ flex: 1, overflowY: "auto", paddingBottom: 40 }}>
        {loading && <div className="p" style={{ textAlign: "center", padding: 20 }}>載入中...</div>}

        {!loading && activeTab === "PROJECT" && isSuper && (
          <div style={{ marginBottom: 16, display: "flex", justifyContent: "space-between" }}>
            <button className="btn" style={{ width: "auto", background: "#333" }} onClick={handleRefreshSource} disabled={refreshing}>
              {refreshing ? "更新中..." : "更新資訊"}
            </button>
            <button className="btn" style={{ width: "auto" }} onClick={handleCreateProject}>
              ＋ 新增專案
            </button>
          </div>
        )}

        {!loading && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
              gap: 20,
            }}
          >
            {cards.map((raw) => {
              const card = normalizeComplianceCardForUI(raw);

              const ownerCode =
                DEPARTMENTS.find((d) => d.name === (card as any).ownerDept || d.code === (card as any).ownerDept)?.code ||
                (card as any).ownerDept;
              const isOwner = ownerCode === userDeptCode || (card as any).sectionB?.ownerDept === userDeptCode;

              return ProjectGridItem({
                card,
                onClick: () => setSelectedCard(normalizeComplianceCardForUI(card)),
                onDelete: canDelete ? () => handleDelete(card) : undefined,
                canManage: isSuper,
                isOwner: isOwner,
              });
            })}
          </div>
        )}

        {!loading && cards.length === 0 && (
          <div className="p" style={{ textAlign: "center", padding: 40, color: "var(--muted)" }}>
            {activeTab === "HISTORY" ? "目前尚無歷史彙編資料" : "無權存取資料或目前無資料"}
          </div>
        )}
      </div>

      {selectedCard && (
        <Modal
          title={`專案詳情：${selectedCard.sectionA?.title || ""}`}
          onClose={() => setSelectedCard(null)}
          width={1000}
        >
          <ComplianceCard
            card={normalizeComplianceCardForUI(selectedCard)}
            onRefresh={() => {
              loadAllCards();
              setSelectedCard(null);
            }}
            readOnly={activeTab === "HISTORY"}
          />
        </Modal>
      )}

      {showCrawlerConfig && <TrackedAuthoritiesDialog onClose={() => setShowCrawlerConfig(false)} onUpdate={loadAllCards} />}
    </div>
  );
}

